import { Plus } from "lucide-react";
import {
  useGetList,
  useLocaleState,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";

import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import type { CrmEvent } from "../types";
import { getDefaultEventStart, startOfDay, toDayKey } from "./calendarUtils";
import { EventCreateDialog, EventEditDialog } from "./EventDialogs";
import { useEventTypeLookup } from "./eventTypes";
import { formatEventTime } from "./formatEvent";

const UPCOMING_EVENTS_LIMIT = 200;

/** Mobile agenda: upcoming events grouped by day; a month grid is too small on a phone. */
export const EventsPageMobile = () => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const lookupType = useEventTypeLookup();
  const [isCreateOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<Identifier | null>(null);
  // Fixed for the page's lifetime, so the query key stays stable.
  const filter = useMemo(
    () => ({ "start_at@gte": startOfDay(new Date()).toISOString() }),
    [],
  );
  const { data: events = [], isPending } = useGetList<CrmEvent>("events", {
    filter,
    pagination: { page: 1, perPage: UPCOMING_EVENTS_LIMIT },
    sort: { field: "start_at", order: "ASC" },
  });
  const days = groupByDay(events);
  const dayLabel = new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <>
      <MobileHeader showRefresh>
        <div className="flex flex-1 items-center justify-between">
          <h1 className="text-xl font-semibold">
            {translate("resources.events.name", { smart_count: 2 })}
          </h1>
          <Button
            variant="ghost"
            size="icon"
            aria-label={translate("resources.events.action.create")}
            onClick={() => setCreateOpen(true)}
          >
            <Plus className="size-5" />
          </Button>
        </div>
      </MobileHeader>
      <MobileContent>
        {!isPending && events.length === 0 && (
          <p className="text-muted-foreground py-6 text-center">
            {translate("crm.dashboard.no_upcoming_events")}
          </p>
        )}
        <div className="flex flex-col gap-5">
          {days.map(([dayKey, dayEvents]) => (
            <section key={dayKey}>
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                {dayLabel.format(new Date(dayEvents[0].start_at))}
              </h2>
              <ul className="flex flex-col gap-2">
                {dayEvents.map((event) => {
                  const type = lookupType(event.type);
                  return (
                    <li key={event.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedId(event.id)}
                        className="w-full text-left rounded-lg border bg-card px-3 py-2 border-l-4"
                        style={{ borderLeftColor: type.color }}
                      >
                        <div className="font-medium">{event.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {formatEventTime(new Date(event.start_at), locale)}
                          {event.end_at
                            ? ` – ${formatEventTime(new Date(event.end_at), locale)}`
                            : ""}
                          {type.label ? ` · ${type.label}` : ""}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      </MobileContent>
      <EventCreateDialog
        open={isCreateOpen}
        record={{ start_at: getDefaultEventStart(new Date()) }}
        onClose={() => setCreateOpen(false)}
      />
      <EventEditDialog
        eventId={selectedId}
        onClose={() => setSelectedId(null)}
      />
    </>
  );
};

const groupByDay = (events: CrmEvent[]): [string, CrmEvent[]][] => {
  const byDay = new Map<string, CrmEvent[]>();
  events.forEach((event) => {
    const key = toDayKey(new Date(event.start_at));
    byDay.set(key, [...(byDay.get(key) ?? []), event]);
  });
  return [...byDay.entries()];
};
